import React, { useMemo, useContext, useCallback, createContext } from 'react'
import { PaginationReq, CashLogQuery, AdminCashLogType } from '@golden/gdk-admin'
import { omitBy, isUndefined } from '@golden/utils'
import { startOfDay, endOfDay, subYears, startOfYear, getTime } from 'date-fns'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import MuiButton from '@material-ui/core/Button'
import DateInputBase, { DateInputValue } from '../../default/form/DateInput'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import MultipleSelector, { PropTypes as MultipleSelectorProps } from '../../default/form/MultipleSelector'
import FormStateProvider from '../../default/form/FormStateProvider'
import FormField from '../../default/form/FormField'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { InitialFormFunc, useRequestFromSearch, useChangeUrlSubmit, SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { ValueGetter, createDefaultFormState, FormValidation } from '../../../utils/default/FormHook'
import { getTimeFromDateInputValue, getValueFromChangeEvent, convertEmptyToUndefined, guaranteeNotUndefined, guaranteeBetween, pipe, parseInt, getValueFromValue, parseArray, acceptUndefined } from '../../../utils/default/FormHelper'
import { createShouldDisableDate, setToday, setYesterday, setThisWeek, setLastWeek, setThisMonth, setLastMonth, setLastTwoMonths } from '@golden/shared'
import allRoute from '../route/route'
import adminCashLogName from '../../../constants/admin/adminCashLogName'

export type PlayerReportCashRequest = PaginationReq & CashLogQuery

export interface PlayerReportCashFormType {
  time: DateInputValue
  account: string
  order_number: string
  categories: AdminCashLogType[]
}

export const initialForm: InitialFormFunc<PlayerReportCashFormType> = (defaultForm) => ({
  time: {
    start: startOfDay(new Date()),
    end: endOfDay(new Date())
  },
  account: '',
  order_number: '',
  categories: [],
  ...defaultForm
})

const getValueFromEvent: ValueGetter<PlayerReportCashFormType> = {
  time: getTimeFromDateInputValue,
  account: getValueFromChangeEvent,
  order_number: getValueFromChangeEvent,
  categories: getValueFromValue
}

const formToRequest = (form: PlayerReportCashFormType): PlayerReportCashRequest => {
  const converted = {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    start_at: getTime(form.time.start!),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    end_at: getTime(form.time.end!),
    account: convertEmptyToUndefined(form.account),
    order_number: convertEmptyToUndefined(form.order_number),
    categories: form.categories.length ? form.categories : undefined,
    page: 1
  } as PlayerReportCashRequest
  const omited = omitBy(converted, isUndefined)
  return omited as PlayerReportCashRequest
}

export const searchToRequest: SearchToRequestFunc<PlayerReportCashRequest> = (search) => {
  const converted = {
    ...search,
    page: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.page),
    start_at: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.start_at),
    end_at: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.end_at),
    categories: acceptUndefined(search.categories, pipe(
      parseArray
    ))
  } as PlayerReportCashRequest
  if (converted.end_at < converted.start_at) throw new Error('The end time can\'t exceed the start time')
  return omitBy(converted, isUndefined) as PlayerReportCashRequest
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const TextField = React.memo(MuiTextField)

const Button = React.memo(MuiButton)

const DateInput: React.FC = React.memo(() => {
  const classes = useCommonStyles()
  const { t } = useT()
  const { value, handleChange } = useContext(FormContext)
  const {
    shouldDisableStartDate,
    shouldDisableEndDate
  } = useMemo(() => {
    const fiveYearsAgo = startOfYear(subYears(new Date(), 5))
    return createShouldDisableDate(value.time.start, value.time.end, fiveYearsAgo)
  }, [value.time])
  const tools = useMemo(() => {
    return [
      {
        label: t('common.today'),
        change: setToday
      },
      {
        label: t('common.yesterday'),
        change: setYesterday
      },
      {
        label: t('common.thisWeek'),
        change: setThisWeek
      },
      {
        label: t('common.lastWeek'),
        change: setLastWeek
      },
      {
        label: t('common.thisMonth'),
        change: setThisMonth
      },
      {
        label: t('common.lastMonth'),
        change: setLastMonth
      },
      {
        label: t('common.pastTwoMonths'),
        change: setLastTwoMonths
      }
    ]
  }, [t])
  const startOption = useMemo(() => ({
    label: t('common.beginAt'),
    shouldDisableDate: shouldDisableStartDate
  }), [shouldDisableStartDate, t])

  const endOption = useMemo(() => ({
    label: t('common.endAt'),
    shouldDisableDate: shouldDisableEndDate
  }), [shouldDisableEndDate, t])

  const dateClasses = useMemo(() => ({
    button: classes.pinkGradualButton
  }), [classes.pinkGradualButton])

  const onChange = useCallback(handleChange('time'), [])
  return (
    <DateInputBase
      value={value.time}
      onChange={onChange}
      start={startOption}
      end={endOption}
      tools={tools}
      classes={dateClasses}
    />
  )
})

const PlayerReportCashForm: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const pageFlow = usePageFlow()

  const request = useRequestFromSearch({ searchToRequest })

  const defaultForm = useMemo(() => {
    if (request) {
      return initialForm({
        time: {
          start: new Date(request.start_at),
          end: new Date(request.end_at)
        },
        ...request
      })
    }
    return initialForm()
  }, [request])

  const handleSubmit = useChangeUrlSubmit({
    toAddNowTimestamp: true,
    formToRequest,
    encodePath: allRoute.playerReportCash.encodePath
  })

  const validation = useMemo(() => ({
    time: [],
    account: [],
    order_number: [],
    categories: []
  } as FormValidation<PlayerReportCashFormType>), [])

  const typeOptions = Object.keys(adminCashLogName)
    .map((key) => ({
      name: t(adminCashLogName[key as AdminCashLogType]),
      value: key
    }))
  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultForm}
      onSubmit={handleSubmit}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
    >
      <Paper>
        <Box padding={4}>
          <LoadingAndErrorFrame { ...pageFlow.status }>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Box
                  paddingY={1.25}
                  paddingX={2}
                  className={classes.pinkTitleBar}
                >
                  <Typography variant="h5">
                    {t('page.cashLog')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <DateInput />
              </Grid>
              <Grid item>
                <Grid container direction="row" alignItems="flex-end" spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormField<PlayerReportCashFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="account"
                      label={t('common.playerAccount')}
                      placeholder={t('placeholder.inputPlayerAccount')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<PlayerReportCashFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="order_number"
                      label={t('common.serialNumber')}
                      placeholder={t('placeholder.inputSerialNumber')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormField<PlayerReportCashFormType, MultipleSelectorProps>
                      context={FormContext}
                      component={MultipleSelector}
                      name="categories"
                      label={t('common.cashLogType')}
                      options={typeOptions}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <FormSubmitButton
                      component={Button}
                      context={FormContext}
                      type="submit"
                      className={classes.purpleGradualButton}
                    >
                      {t('common.search')}
                    </FormSubmitButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </LoadingAndErrorFrame>
        </Box>
      </Paper>
    </FormStateProvider>
  )
}

export default React.memo(PlayerReportCashForm)
